/**
 * DateRangePicker Component
 *
 * A date range picker with preset options and custom date inputs.
 * Built with Radix UI Popover.
 *
 * @see types/accounting.ts for TransactionFilters type
 */

import { useState, useCallback, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import { Calendar, ChevronDown, X } from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  startOfYear,
  format,
  parse,
  isValid,
  isAfter,
} from 'date-fns';

export interface DatePreset {
  label: string;
  from: Date;
  to: Date;
}

export interface DateRangePickerProps {
  /** Start date in ISO format (YYYY-MM-DD) or null */
  from: string | null;
  /** End date in ISO format (YYYY-MM-DD) or null */
  to: string | null;
  /** Callback when dates change */
  onChange: (from: string | null, to: string | null) => void;
  /** Custom date presets (defaults to standard presets) */
  presets?: DatePreset[];
  /** Placeholder text when no dates selected */
  placeholder?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Default date presets
 */
function getDefaultPresets(): DatePreset[] {
  const now = new Date();
  return [
    {
      label: 'This Month',
      from: startOfMonth(now),
      to: endOfMonth(now),
    },
    {
      label: 'Last Month',
      from: startOfMonth(subMonths(now, 1)),
      to: endOfMonth(subMonths(now, 1)),
    },
    {
      label: 'This Quarter',
      from: startOfQuarter(now),
      to: endOfQuarter(now),
    },
    {
      label: 'Last Quarter',
      from: startOfQuarter(subQuarters(now, 1)),
      to: endOfQuarter(subQuarters(now, 1)),
    },
    {
      label: 'Year to Date',
      from: startOfYear(now),
      to: now,
    },
    {
      label: 'Last 30 Days',
      from: subMonths(now, 1),
      to: now,
    },
    {
      label: 'Last 90 Days',
      from: subMonths(now, 3),
      to: now,
    },
  ];
}

/**
 * Format a date for display
 */
function formatDateDisplay(date: Date): string {
  return format(date, 'dd MMM yyyy');
}

/**
 * Format a date for input value (YYYY-MM-DD)
 */
function formatDateValue(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Parse a date string from input
 */
function parseDateInput(value: string): Date | null {
  if (!value) return null;
  const date = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(date) ? date : null;
}

/**
 * DateRangePicker renders a popover with date range selection.
 *
 * Features:
 * - Quick presets for common date ranges
 * - Custom from/to date inputs
 * - Validates that from <= to
 * - Clears dates with X button
 * - Keyboard accessible
 *
 * @example
 * ```tsx
 * <DateRangePicker
 *   from={filters.dateFrom}
 *   to={filters.dateTo}
 *   onChange={(from, to) => onFilterChange({ dateFrom: from, dateTo: to })}
 * />
 * ```
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  presets,
  placeholder = 'Select date range...',
  disabled = false,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [fromInput, setFromInput] = useState(from || '');
  const [toInput, setToInput] = useState(to || '');
  const [error, setError] = useState<string | null>(null);

  // Use provided presets or defaults
  const datePresets = useMemo(() => presets || getDefaultPresets(), [presets]);

  // Parse current values for display
  const fromDate = from ? parseDateInput(from) : null;
  const toDate = to ? parseDateInput(to) : null;

  // Format display text
  const displayText = useMemo(() => {
    if (fromDate && toDate) {
      return `${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}`;
    }
    if (fromDate) {
      return `From ${formatDateDisplay(fromDate)}`;
    }
    if (toDate) {
      return `To ${formatDateDisplay(toDate)}`;
    }
    return placeholder;
  }, [fromDate, toDate, placeholder]);

  const hasValue = from || to;

  // Handle preset selection
  const handlePresetClick = useCallback(
    (preset: DatePreset) => {
      const newFrom = formatDateValue(preset.from);
      const newTo = formatDateValue(preset.to);
      onChange(newFrom, newTo);
      setFromInput(newFrom);
      setToInput(newTo);
      setError(null);
      setOpen(false);
    },
    [onChange]
  );

  // Handle from date input change
  const handleFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInput(e.target.value);
    setError(null);
  }, []);

  // Handle to date input change
  const handleToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setToInput(e.target.value);
    setError(null);
  }, []);

  // Apply custom date range
  const handleApply = useCallback(() => {
    const parsedFrom = parseDateInput(fromInput);
    const parsedTo = parseDateInput(toInput);

    // Validate dates
    if (fromInput && !parsedFrom) {
      setError('Invalid from date');
      return;
    }
    if (toInput && !parsedTo) {
      setError('Invalid to date');
      return;
    }
    if (parsedFrom && parsedTo && isAfter(parsedFrom, parsedTo)) {
      setError('From date must be before to date');
      return;
    }

    onChange(fromInput || null, toInput || null);
    setError(null);
    setOpen(false);
  }, [fromInput, toInput, onChange]);

  // Clear dates
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null, null);
      setFromInput('');
      setToInput('');
      setError(null);
    },
    [onChange]
  );

  // Sync inputs when popover opens
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        setFromInput(from || '');
        setToInput(to || '');
        setError(null);
      }
      setOpen(newOpen);
    },
    [from, to]
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={clsx(
            'flex items-center gap-2 px-3 py-2',
            'text-sm text-left',
            'border rounded-md shadow-sm',
            'bg-white',
            'transition-colors duration-150',
            disabled
              ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
              : 'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'border-gray-300',
            className
          )}
          disabled={disabled}
          aria-label={displayText}
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid="date-range-picker-trigger"
        >
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <span
            className={clsx(
              'truncate flex-1',
              !hasValue && 'text-gray-500'
            )}
          >
            {displayText}
          </span>
          {hasValue && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e as unknown as React.MouseEvent);
                }
              }}
              className="p-0.5 rounded hover:bg-gray-100 cursor-pointer"
              aria-label="Clear dates"
            >
              <X className="w-3 h-3 text-gray-400" />
            </span>
          )}
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200',
              open && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={clsx(
            'w-80 bg-white rounded-lg shadow-lg border border-gray-200',
            'z-50 p-4',
            'animate-in fade-in-0 zoom-in-95'
          )}
          sideOffset={4}
          align="start"
          data-testid="date-range-picker-popover"
        >
          {/* Presets */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Quick Select
            </div>
            <div className="flex flex-wrap gap-1">
              {datePresets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className={clsx(
                    'px-2 py-1 text-xs rounded',
                    'border border-gray-200',
                    'hover:bg-gray-50 hover:border-gray-300',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500'
                  )}
                  data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-4" />

          {/* Custom date inputs */}
          <div className="space-y-3">
            <div>
              <label
                htmlFor="date-from"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                From
              </label>
              <input
                id="date-from"
                type="date"
                value={fromInput}
                onChange={handleFromChange}
                className={clsx(
                  'w-full px-3 py-2 text-sm',
                  'border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'border-gray-300'
                )}
                data-testid="date-from-input"
              />
            </div>

            <div>
              <label
                htmlFor="date-to"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                To
              </label>
              <input
                id="date-to"
                type="date"
                value={toInput}
                onChange={handleToChange}
                className={clsx(
                  'w-full px-3 py-2 text-sm',
                  'border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'border-gray-300'
                )}
                data-testid="date-to-input"
              />
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={clsx(
                  'flex-1 px-3 py-2 text-sm',
                  'border rounded-md',
                  'text-gray-700 hover:bg-gray-50',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className={clsx(
                  'flex-1 px-3 py-2 text-sm',
                  'bg-blue-500 text-white rounded-md',
                  'hover:bg-blue-600',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                )}
                data-testid="date-range-apply"
              >
                Apply
              </button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default DateRangePicker;
